import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import dayjs from 'dayjs';
import { SilentError } from 'init/silent-error';
import { isDefined } from 'remeda';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { CurrencyCodeSchema } from 'types/currency';
import { PaginationSchema } from 'types/pagination';
import { z } from 'zod/v4';

const URL = '/group/debtors';

const OutstandingSchema = z.partialRecord(CurrencyCodeSchema, z.string());

const AgedBalanceSchema = z.object({
  not_due: OutstandingSchema,
  '0': OutstandingSchema,
  '30': OutstandingSchema,
  '60': OutstandingSchema,
  '90': OutstandingSchema,
});

const GroupDebtorCompanySchema = z.object({
  subdomain: z.string(),
  outstanding: OutstandingSchema,
  agedBalance: AgedBalanceSchema,
  agedBalanceComputedAt: z.iso.datetime().transform(dayjs).nullable(),
});

const GroupDebtorSchema = z.object({
  id: z.string(),
  fullName: z.string(),
  vatNumber: z.string().nullable(),
  siren: z.string().nullable(),
  reference: z.string().nullable(),
  outstanding: OutstandingSchema,
  companies: z.array(GroupDebtorCompanySchema),
  worstPaymentDelay: z.number().nullable(),
  agedBalance: AgedBalanceSchema,
  agedBalanceComputedAt: z.iso.datetime().transform(dayjs).nullable(),
});

const GroupDebtorsSchema = z.object({
  debtors: z.array(GroupDebtorSchema),
  currencies: z.array(CurrencyCodeSchema),
  pagination: PaginationSchema,
});

export type GroupDebtor = z.infer<typeof GroupDebtorSchema>;
export type GroupDebtors = z.infer<typeof GroupDebtorsSchema>;

export type LoadGroupDebtorsVariables = {
  page: number;
  pageLimit: number;
  name?: string;
  subdomains?: string[];
};

async function loadGroupDebtorsQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  { page, pageLimit, name, subdomains }: LoadGroupDebtorsVariables,
) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL, {
    params: { page, page_limit: pageLimit, name, subdomains },
  });

  const parseResult = GroupDebtorsSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'groupDebtors');
    throw new SilentError();
  }

  return parseResult.data;
}

const emptyGroupDebtors: GroupDebtors = {
  debtors: [],
  currencies: [],
  pagination: { totalObjects: 0, totalPages: 0, pageLimit: 0 },
};

export function useLoadGroupDebtors(
  variables: LoadGroupDebtorsVariables,
  options: QueryOptions<GroupDebtors> = {},
) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<GroupDebtors, ApiError>({
    queryKey: ['groupDebtors', variables],
    queryFn: () => loadGroupDebtorsQueryFn(axiosInstance, variables),
    placeholderData: previousData => {
      if (isDefined(previousData)) return previousData;
      return emptyGroupDebtors;
    },
    ...options,
  });

  return addResourceNameToQueryResult<GroupDebtors, ApiError, 'groupDebtors', 'PreventUndefined'>(
    'groupDebtors',
    queryResult,
    emptyGroupDebtors,
  );
}
